import {
  concat,
  empty,
  Observable,
  ObservableInput,
  of,
  Subscription
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ContentItemWithLayout, Site } from './../../api';
import {
  AnyJson,
  dbgAddSource,
  EMPTY_JSON_OBSERVABLE,
  freezeDeep,
  generateItem,
  Generator,
  isFunction,
  isNil,
  isNotNil,
  isObservable,
  isPlainObject,
  isString,
  jsonParse
} from './../../utils';
import {
  assertIsString,
  assertNotNil,
  assertTrue,
  isAssertOn
} from './../internal/assert';
import { lid, lw } from './../services/logger/logger.service';
import { wchGetSiteURL, wchGetRenderingContextURL } from './wch.utils';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'BootstrapUtils';

const _addDebug = <T>(aObject: T) => dbgAddSource<T>(LOGGER, aObject);

export type BootstrapSource<T> = T | string | ObservableInput<T>;
export type BootstrapInput<T> =
  | BootstrapSource<T>
  | Generator<BootstrapSource<T>>;

/**
 * Tests if the value is a valid input
 *
 * @param aValue    the value
 * @return true if the value is valid
 */
function _isBootstrapInput<T>(aValue: any): aValue is BootstrapInput<T> {
  // test
  return (
    isPlainObject(aValue) ||
    isString(aValue) ||
    isFunction(aValue) ||
    isObservable(aValue)
  );
}

/*
 * Our actual adata cache from identifier to Observable.
 */
const data: { [key: string]: Observable<AnyJson> } = {};

/**
 * Converts the input to an observable of the desired type
 *
 * @param aValue    the value to convert
 * @return the type
 */
function _bootstrapInputToObservable<T>(
  aValue: BootstrapInput<T>
): Observable<T> {
  // validate key and value
  if (isNotNil(aValue)) {
    // parse the string
    if (isString(aValue)) {
      // construct an observable
      return of(jsonParse<T>(aValue));
    }
    // handle the generator
    if (isFunction(aValue)) {
      // construct an observable
      return _generatorToObservable<T>(aValue as Generator<BootstrapSource<T>>);
    }
    // simple objec
    if (isPlainObject(aValue)) {
      // assume an observable input
      return of((aValue as any) as T);
    }
    // assume an observable input
    return concat((aValue as any) as ObservableInput<T>);
  }
  // nothing
  return empty();
}

/**
 * Converts a generator, the result of the generator can be another input
 *
 * @param aGenerator    the generator
 * @return the observable
 */
function _generatorToObservable<T>(
  aGenerator: Generator<BootstrapSource<T>>
): Observable<T> {
  // generate the sequence
  return generateItem<BootstrapSource<T>>(aGenerator).pipe(
    switchMap(_bootstrapInputToObservable)
  );
}

/*
 * Adds bootstrap data to the cache
 *
 * @param aKey the key
 * @param aValue the value as a string
 *
 * @return an observable for the result or the empty observable
 */
function _internalPut(aKey: string, aValue: BootstrapInput<AnyJson>) {
  // log this
  lid(LOGGER, 'Put', aKey, isNotNil(aValue));
  // validate key and value
  if (isNotNil(aKey) && isNotNil(aValue)) {
    // check
    data[aKey] = _bootstrapInputToObservable(aValue).pipe(
      map(_addDebug),
      map(freezeDeep)
    );
  }
}

/*
 * Clears all registered records
 */
function _bootstrapClear() {
  // tslint:disable-next-line:forin
  for (const key in data) {
    delete data[key];
  }
}

/*
 * Retrieves the bootstrap data from the cache
 *
 * @param aKey the key
 * @return an observable for the result or the empty observable
 */
function _bootstrapGet(aKey: string): Observable<AnyJson> {
  // access the data record
  const result = data[aKey];
  // log this
  lid(LOGGER, 'Accessing data from bootstrap', aKey, isNotNil(result));
  // done
  return isNil(result) ? EMPTY_JSON_OBSERVABLE : result;
}

/*
 * Adds bootstrap data to the cache
 *
 * @param aKey the key
 * @param aValue the value as a string
 */
function _bootstrapPut(
  aKey: string,
  aValue: AnyJson | string | ObservableInput<AnyJson> | Generator<AnyJson>
) {
  // some assertions
  if (isAssertOn()) {
    assertIsString(aKey);
    assertNotNil(aValue);
  }
  // dispatch
  _internalPut(aKey, aValue);
}

/*
 * Remove bootstrap data from the cache
 *
 * @param aKey the key
 * @return an observable for the result or the empty observable
 */
function _bootstrapRemove(aKey: string) {
  // some assertions
  if (isAssertOn()) {
    assertIsString(aKey);
  }
  // dispatch
  delete data[aKey];
}

/*
 * Registers a rendering context. The key is computed based on the ID of the context
 *
 * @param aContext the context to register
 */
function _bootstrapPutContentWithLayout(
  aContext: ContentItemWithLayout | string
): Subscription | undefined {
  // some assertions
  if (isAssertOn()) {
    assertNotNil(aContext);
    assertTrue(isPlainObject(aContext) || isString(aContext));
  }
  // validate that we have content
  if (isNotNil(aContext)) {
    // in case of a string we need to parse it to get the ID
    if (isString(aContext)) {
      // convert to a context
      const stringCtx: string = aContext as string;
      const parsedContext = jsonParse(stringCtx) as ContentItemWithLayout;
      return wchGetRenderingContextURL(parsedContext.id).subscribe(url =>
        _internalPut(url, parsedContext)
      );
    } else if (isPlainObject(aContext)) {
      // extract the ID, then stringify
      const jsonContext: ContentItemWithLayout = aContext as ContentItemWithLayout;
      return wchGetRenderingContextURL(jsonContext.id).subscribe(url =>
        _internalPut(url, jsonContext)
      );
    } else {
      // warn
      lw(LOGGER, 'Context is neither string nor plain object.', aContext);
    }
  } else {
    // warn
    lw(LOGGER, 'Context is null.');
  }
  // nothing to return
  return undefined;
}

/*
 * Registers a site. The key is computed based on the ID of the site
 *
 * @param aSite the context to register
 * @param aSiteId optionaly the ID of the site
 *
 * @return the subscription
 */
function _bootstrapPutSite(
  aInput: BootstrapInput<Site>,
  aSiteId?: string
): Subscription | undefined {
  // some assertions
  if (isAssertOn()) {
    assertNotNil(aInput);
    assertTrue(_isBootstrapInput(aInput));
  }
  // validate that we have a site
  if (isNotNil(aInput)) {
    // test if we have an ID
    if (isString(aSiteId)) {
      // add the site
      return wchGetSiteURL().subscribe(url => _internalPut(url, aInput));
    }
    // in case of a string we need to parse it to get the ID
    else if (isString(aInput)) {
      // convert to a context
      const stringSite: string = aInput as string;
      const parsedSite = jsonParse<Site>(stringSite);
      // add the site
      return wchGetSiteURL().subscribe(url => _internalPut(url, parsedSite));
    } else if (isPlainObject(aInput)) {
      // extract the ID, then stringify
      const jsonSite: Site = aInput as Site;
      return wchGetSiteURL().subscribe(url => _internalPut(url, jsonSite));
    } else {
      // warn
      lw(LOGGER, 'Site is neither string nor plain object.', aInput);
    }
  } else {
    // warn
    lw(LOGGER, 'Site is null.');
  }
  // no subscription
  return undefined;
}

export {
  _bootstrapGet as bootstrapGet,
  _bootstrapPutContentWithLayout as bootstrapPutContentWithLayout,
  _bootstrapPut as bootstrapPut,
  _bootstrapPutSite as bootstrapPutSite,
  _bootstrapClear as bootstrapClear
};
